"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Button } from "../../elements/button/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../elements/dialog/dialog";
import { RadioGroup } from "../../elements/radio-group/radio-group";
import { Text } from "../../elements/text/text";
import {
  type UserAddressFormData,
  userAddressModalSchema,
} from "./user-address-modal.schema";

export interface UserAddressData {
  id: string;
  name: string;
  street: string;
  city: string;
  postalCode: string;
  country: string;
}

export interface UserAddressModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  microcopies: {
    title: string;
    description: string;
    saveButtonLabel: string;
  };
  addresses: UserAddressData[];
  defaultAddressId?: string;
  onSubmit: (data: UserAddressFormData) => Promise<void> | void;
  isPending?: boolean;
}

export function UserAddressModal(props: Readonly<UserAddressModalProps>) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isValid },
  } = useForm<UserAddressFormData>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference is incompatible with this Zod schema
    resolver: zodResolver(userAddressModalSchema as any),
    values: {
      addressId: props.defaultAddressId ?? "",
    },
    mode: "onChange",
  });

  const onSubmit = async (data: UserAddressFormData) => {
    await props.onSubmit(data);
    props.onOpenChange(false);
    reset(data);
  };

  const handleOpenChange = (open: boolean) => {
    props.onOpenChange(open);
    if (!open) {
      reset({ addressId: props.defaultAddressId ?? "" });
    }
  };

  const radioOptions = props.addresses.map((addr) => ({
    value: addr.id,
    label: (
      <Text variant={"body-md"}>
        {addr.name}
        <br />
        {addr.street}
        <br />
        {addr.postalCode} {addr.city}
        <br />
        {addr.country}
      </Text>
    ),
  }));

  return (
    <Dialog onOpenChange={handleOpenChange} open={props.open}>
      <DialogContent className="sm:max-w-[532px]">
        <DialogHeader>
          <DialogTitle>{props.microcopies.title}</DialogTitle>
          <DialogDescription>{props.microcopies.description}</DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="max-h-[50vh] overflow-y-auto p-2">
            <Controller
              control={control}
              name="addressId"
              render={({ field }) => (
                <RadioGroup
                  onValueChange={field.onChange}
                  options={radioOptions}
                  value={field.value}
                />
              )}
            />
          </div>

          <div className="flex justify-end pt-4">
            <Button
              disabled={props.isPending || isSubmitting || !isValid}
              loading={isSubmitting || props.isPending}
              type="submit"
            >
              {props.microcopies.saveButtonLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
